'use client';

import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import LoopRoundedIcon from '@mui/icons-material/LoopRounded';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { PUZZLE_LENGTH } from '@wt/app/game/_components/PuzzleLoader';
import ConfirmationModal from '@wt/game/modals/ConfirmationModal';
import GuidelinesModal from '@wt/game/modals/GuidelinesModal';
import { useCallback, useMemo } from 'react';

import { CompletedIcon } from './CompletedIcon';
import { useGameFormProvider } from './GameFormProvider';
import { PicturesInputs } from './PicturesInputs';
import { SettingsInputs } from './SettingsInputs';

export const GameForm = () => {
  const {
    showConfirmationModal,
    setShowConfirmationModal,
    deleteGame,
    showGuidelinesModal,
    closeGuidelinesModal,
    firstTimeViewingGuidelinesModal,
  } = useGameFormProvider();

  return (
    <div className="flex w-full flex-col items-center gap-6">
      <SettingsInputs />
      <PicturesInputs />
      <SaveInfoText />
      <>
        <div className="hidden w-full flex-row justify-between md:flex">
          <DeleteGameOrDraftButton />
          <SaveGameButton />
        </div>
        <div className="flex w-full flex-col items-center gap-2 md:hidden">
          <SaveGameButton />
          <DeleteGameOrDraftButton />
        </div>
      </>
      {showConfirmationModal && (
        <ConfirmationModal
          closeModal={() => setShowConfirmationModal(false)}
          infoText="Are you sure you want to delete this game?"
          buttonLabel="Delete game"
          handleClick={deleteGame}
        />
      )}
      {showGuidelinesModal && (
        <GuidelinesModal
          closeModal={closeGuidelinesModal}
          firstTimeViewing={firstTimeViewingGuidelinesModal}
        />
      )}
    </div>
  );
};

const SaveInfoText = () => {
  const { formDataValid, imagesUploaded, isEdit, hasChanges } =
    useGameFormProvider();

  return (
    <div className="flex w-full flex-col gap-2 text-start text-sm">
      <span>
        To save the game and be able to share it, you have to upload 5 images
        and provide all the necessary information.
      </span>
      <div className="flex flex-row gap-2">
        <CompletedIcon completed={imagesUploaded === PUZZLE_LENGTH} />{' '}
        {`${imagesUploaded}/${PUZZLE_LENGTH} images uploaded`}
      </div>
      <div className="flex flex-row gap-2">
        <CompletedIcon completed={formDataValid} /> All details filled out
      </div>
      {isEdit && (
        <div className="flex flex-row gap-2">
          <CompletedIcon completed={hasChanges} /> Has changes
        </div>
      )}
    </div>
  );
};

const SaveGameButton = () => {
  const { formDataValid, loading, saveGame, hasChanges, deleting } =
    useGameFormProvider();

  return (
    <button
      onClick={saveGame}
      disabled={!formDataValid || !hasChanges || loading || deleting}
      className="btn btn-primary mt-2 h-[40px] min-h-[40px] w-fit shrink whitespace-nowrap rounded-lg text-base font-medium disabled:bg-[#CBC7D1] disabled:text-white"
    >
      <span className="pointer-events-none whitespace-nowrap">
        {loading ? (
          <LoopRoundedIcon className="animate-spin" />
        ) : (
          <SaveOutlinedIcon className="pr-1" />
        )}{' '}
        Save game
      </span>
    </button>
  );
};

const DeleteGameOrDraftButton = () => {
  const {
    isEdit,
    loading,
    setShowConfirmationModal,
    deleting,
    deleteDraft,
    hasDraft,
  } = useGameFormProvider();

  const deleteGameOrDraft = useCallback(() => {
    if (isEdit) {
      setShowConfirmationModal(true);
    } else {
      deleteDraft();
    }
    return;
  }, [isEdit]);

  const buttonText = useMemo(() => {
    if (isEdit) {
      return 'Delete game';
    } else {
      return 'Delete draft';
    }
  }, [isEdit]);

  return (
    <button
      onClick={deleteGameOrDraft}
      disabled={loading || deleting || !hasDraft}
      className="btn btn-primary mt-2 h-[40px] min-h-[40px] w-fit shrink whitespace-nowrap rounded-lg border-none bg-red-500 text-base font-medium hover:bg-red-600 disabled:bg-[#CBC7D1] disabled:text-white"
    >
      <span className="pointer-events-none whitespace-nowrap">
        {deleting ? (
          <LoopRoundedIcon className="animate-spin" />
        ) : (
          <DeleteOutlineRoundedIcon className="pr-1" />
        )}{' '}
        {buttonText}
      </span>
    </button>
  );
};
